import { addDays, format, max, parseISO, startOfDay, subDays } from 'date-fns';
import type { Language } from '@/domainLogic/index.ts';
import {
  APEX_FLEXIBLE_DAYS_EXACT,
  APEX_FLEXIBLE_DAYS_OPTIONS,
  type ApexFlexibleDays,
} from '@/app/common/apex/apexFlexibleDays.ts';
import {
  decorateShortDate,
  decorateShortDateDay,
  isSameMonth,
} from '@/app/components/search/components/EvolutionCard/utils/getDecorators.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';

const URL_DATE_FORMAT = 'yyyy-MM-dd';

export type DepartureWindow = { from: string; to: string };

/**
 * The departure window a given flexibility opens up: the searched departure date ±days, with the
 * trip length untouched. Clamped at today, so a search leaving in three days never advertises
 * departures in the past.
 */
export const getDepartureWindow = ({
  dateFrom,
  days,
  today = new Date(),
}: {
  dateFrom: string;
  days: ApexFlexibleDays;
  today?: Date;
}): DepartureWindow => {
  const departure = startOfDay(parseISO(dateFrom));
  const from = max([subDays(departure, days), startOfDay(today)]);

  return { from: format(from, URL_DATE_FORMAT), to: format(addDays(departure, days), URL_DATE_FORMAT) };
};

/**
 * The decorators below do `new Date(d)`, which reads `yyyy-MM-dd` as UTC midnight and then formats it
 * in the runtime's own zone — one day early for anyone behind UTC, and a server/client disagreement
 * now that the switcher is server-rendered. Slashes make the same day parse as local midnight
 * everywhere, so the window reads as the calendar date it is (same trick as
 * getDateWithoutTimeFromYYYYMMDD).
 */
const asLocalDate = (d: string): string => d.replace(/-/g, '/');

/** "13 Aug" for a single day, "10 – 16 Aug" within one month, "28 Aug – 3 Sep" across two. */
export const formatDepartureWindow = ({
  window: { from, to },
  language,
  locale,
}: {
  window: DepartureWindow;
  language: Language;
  locale: string;
}): string => {
  const fromLocal = asLocalDate(from);
  const toLocal = asLocalDate(to);

  if (from === to) return decorateShortDate({ d: fromLocal, language, locale });

  const fromLabel = isSameMonth(fromLocal, toLocal, language, locale)
    ? decorateShortDateDay({ d: fromLocal, language, locale })
    : decorateShortDate({ d: fromLocal, language, locale });

  return `${fromLabel} – ${decorateShortDate({ d: toLocal, language, locale })}`;
};

export const getFlexibilityLabel = ({ days, t }: { days: ApexFlexibleDays; t: TranslationFunction }): string => {
  if (days === APEX_FLEXIBLE_DAYS_EXACT) return t('vacation.oss.flexible_dates.exact_dates');
  if (days === 1) return t('vacation.oss.flexible_dates.plus_minus_day');
  return t('vacation.oss.flexible_dates.plus_minus_days', { days: `${days}` });
};

export type FlexibilityOption = {
  days: ApexFlexibleDays;
  label: string;
  /** The departure window this option would search, already formatted for display. */
  windowLabel: string;
  selected: boolean;
};

export const getFlexibilityOptions = ({
  dateFrom,
  selectedDays,
  language,
  locale,
  t,
}: {
  dateFrom: string;
  selectedDays: ApexFlexibleDays;
  language: Language;
  locale: string;
  t: TranslationFunction;
}): FlexibilityOption[] =>
  ([APEX_FLEXIBLE_DAYS_EXACT, ...APEX_FLEXIBLE_DAYS_OPTIONS] as ApexFlexibleDays[]).map((days) => ({
    days,
    label: getFlexibilityLabel({ days, t }),
    windowLabel: formatDepartureWindow({ window: getDepartureWindow({ dateFrom, days }), language, locale }),
    selected: days === selectedDays,
  }));
